import { API_URL } from '@/config';
import { axios } from '@/lib/axios';
import SparkMD5 from 'spark-md5';
import { ProfileImagePurpose } from '@/features/profile/types';

type DirectUploadResponse = {
  signedId: string;
  key: string;
  filename: string;
  contentType: string;
  directUpload: {
    url: string;
    headers: Record<string, string>;
  };
};

export const PROFILE_IMAGE_ACCEPTED_TYPES = [
  'image/png',
  'image/jpeg',
  'image/gif',
  'image/webp',
] as const;

export const PROFILE_IMAGE_ACCEPT = PROFILE_IMAGE_ACCEPTED_TYPES.join(',');

const computeChecksum = async (file: File) => {
  const buffer = await file.arrayBuffer();
  return btoa(SparkMD5.ArrayBuffer.hash(buffer, true));
};

const createDirectUpload = async (
  purpose: ProfileImagePurpose,
  file: File
): Promise<DirectUploadResponse> => {
  const checksum = await computeChecksum(file);

  return axios.post('/api/v1/direct_uploads', {
    directUpload: {
      purpose,
      filename: file.name,
      byteSize: file.size,
      checksum,
      contentType: file.type,
    },
  });
};

const uploadToStorage = async (
  file: File,
  upload: DirectUploadResponse['directUpload']
) => {
  const uploadUrl = new URL(upload.url, window.location.origin);
  const apiOrigin = API_URL ? new URL(API_URL).origin : null;
  const credentials =
    uploadUrl.origin === window.location.origin ||
    uploadUrl.origin === apiOrigin
      ? 'include'
      : 'omit';

  const response = await fetch(upload.url, {
    method: 'PUT',
    headers: upload.headers,
    body: file,
    credentials,
  });

  if (!response.ok) {
    throw new Error(`direct upload failed with status ${response.status}`);
  }
};

export const uploadProfileImage = async (
  purpose: ProfileImagePurpose,
  file: File
) => {
  const directUpload = await createDirectUpload(purpose, file);
  await uploadToStorage(file, directUpload.directUpload);
  return directUpload.signedId;
};
